import {
    AllEvents,
    ConfigurationContext,
    ElectionDefinition,
    queries,
} from '@news-mono/web-common'
import { useQuery } from '@tanstack/react-query'
import React, { useContext, useMemo } from 'react'
import { getAreaForElection, getPartyColors, PartyColors } from '../data'
import {
    StyledBackgroundBar,
    StyledBarContent,
    StyledBarsContainer,
    StyledCenterLabel,
    StyledCenterLine,
    StyledContainer,
    StyledCountdownText,
    StyledCountdownTextContainer,
    StyledLoadingPlaceholder,
    StyledPartyBar,
    StyledPartyLabel,
    StyledPartyLabelSeatCount,
    StyledPredictedSeats,
    StyledSeatCount,
    StyledSeatCountContainer,
    StyledSeatCountErrorContainer,
    StyledSeatCountErrorText,
    StyledVotesCountedText,
    StyledWonSeats,
    StyledWrapper,
} from './HeadToHeadSeatCountWidget.styled'
import {
    ElectionConfig,
    TransformedData,
} from '@west-australian-newspapers/election-api-types'
import { electionsDebug } from '../../../__helpers/elections-debug'
import { useCountdownTimer } from './useCountdownTimer'

interface PartyResults {
    wonSeats: number
    predictedSeats: number
    partyName?: string
    partyColors: PartyColors
}

interface HeadToHeadSeatCountData {
    right: PartyResults
    left: PartyResults
    seatsToWin: number
    seatsTotal: number
}

const transformToSeatCountData = (
    data: TransformedData,
    config: ElectionConfig,
) => {
    const seatCountData: HeadToHeadSeatCountData = {
        right: {
            wonSeats: 0,
            predictedSeats: 0,
            partyColors: getPartyColors('LNP'),
        },
        left: {
            wonSeats: 0,
            predictedSeats: 0,
            partyColors: getPartyColors('ALP'),
        },
        seatsToWin: config.seatsToWin,
        seatsTotal: config.seatsTotal,
    }

    if (data.areas.length === 0) return

    const headToHeadArea = getAreaForElection(data, config)
    if (!headToHeadArea) {
        electionsDebug(
            'Error loading HeadToHeadSeatCountWidget. No area data found.',
        )
        return
    }
    const configParties = config.headToHeadPartyIds

    // Grab the 2 parties matching the config
    const [partyOne, partyTwo] = headToHeadArea.parties
        .filter((party) => configParties?.includes(party.partyCode))
        .sort((a, b) => (a.partyCode === configParties?.[0] ? -1 : 1)) // Ensure first party in config is always first in the data array (puts it on the left in this case)
    // If there is not 2 parties, then the config is probably wrong
    if (!partyOne || !partyTwo) {
        electionsDebug(
            'Error loading HeadToHeadSeatCountWidget. No matching parties found matching config.',
        )
        return
    }
    // Assumption is the first party is always shown on the left
    seatCountData.left = {
        partyName: partyOne.shortPartyName ?? partyOne.partyName,
        wonSeats: partyOne.seatsWon,
        predictedSeats: partyOne.seatsPredicted,
        partyColors: getPartyColors(partyOne.partyCode),
    }
    seatCountData.right = {
        partyName: partyTwo.shortPartyName ?? partyTwo.partyName,
        wonSeats: partyTwo.seatsWon,
        predictedSeats: partyTwo.seatsPredicted,
        partyColors: getPartyColors(partyTwo.partyCode),
    }

    return seatCountData
}

export interface HeadToHeadSeatCountWidgetProps {
    electionDefinition: ElectionDefinition
    showPredictedSeats?: boolean
    countdownEnabled?: boolean
    onEvent: (event: AllEvents) => void
}

/**
 * Amount of total seats from s3 endpoint data needs to match election definition seatsTotal
 */
export const HeadToHeadSeatCountWidget = ({
    electionDefinition,
    showPredictedSeats = false,
    countdownEnabled = false,
    onEvent,
}: HeadToHeadSeatCountWidgetProps) => {
    const config = useContext(ConfigurationContext)

    const { isSuccess, isError, isLoading, data } = useQuery(
        queries['election-api'].definition({
            initialDefinition: electionDefinition,
            electionAPI: config.electionApi,
            caller: config.apiCallerHeader,
        }),
    )

    const seatCountData = useMemo(() => {
        if (
            !isSuccess ||
            !data.electionData.data ||
            !data.electionData.config
        ) {
            return
        }
        const transformedData = transformToSeatCountData(
            data.electionData.data,
            data.electionData.config,
        )

        return transformedData
    }, [data?.electionData.config, data?.electionData.data, isSuccess])

    // Get the votes counted percentage from the election data

    const votesCountedPercentage = useMemo(() => {
        if (!isSuccess || !data.electionData.data || !data.electionData.config)
            return

        const headToHeadArea = getAreaForElection(
            data.electionData.data,
            data.electionData.config,
        )
        if (!headToHeadArea) return
        return headToHeadArea.pctCounted || (0 as number)
    }, [data?.electionData.config, data?.electionData.data, isSuccess])

    const countdownTargetDate = useMemo(() => {
        const rawElectionDate = data?.electionData.config?.electionDate
        return rawElectionDate ? new Date(rawElectionDate) : undefined
    }, [data?.electionData.config?.electionDate])

    // Error State (data has loaded, but there is no data, or the election date is not set)
    if (
        isError ||
        (isSuccess && !countdownTargetDate) ||
        (isSuccess && countdownTargetDate && !seatCountData)
    ) {
        electionsDebug(
            'Error loading HeadToHeadSeatCountWidget.}',
            isError,
            isSuccess,
            seatCountData,
        )
        return (
            <StyledSeatCountErrorContainer>
                <StyledSeatCountErrorText isBold={true}>
                    Error
                </StyledSeatCountErrorText>
                <StyledSeatCountErrorText>
                    Refresh the page or try again later.
                </StyledSeatCountErrorText>
            </StyledSeatCountErrorContainer>
        )
    }

    // Loading State
    if (isLoading || !seatCountData || !countdownTargetDate) {
        return <StyledLoadingPlaceholder />
    }

    return (
        <HeadToHeadChart
            showPredictedSeats={showPredictedSeats}
            data={seatCountData}
            votesCountedPercentage={votesCountedPercentage}
            showCountdown={countdownEnabled}
            targetDate={countdownTargetDate}
        />
    )
}

type HeadToHeadChartProps = {
    showPredictedSeats?: boolean
    data: HeadToHeadSeatCountData
    votesCountedPercentage: number | undefined
    showCountdown?: boolean
    targetDate: Date
}

const HeadToHeadChart = ({
    data,
    showPredictedSeats,
    votesCountedPercentage,
    showCountdown = false,
    targetDate,
}: HeadToHeadChartProps) => {
    const remainingTime = useCountdownTimer(targetDate)
    const rightPartyTotal = data.right.wonSeats + data.right.predictedSeats
    const leftPartyTotal = data.left.wonSeats + data.left.predictedSeats
    const totalSeats = data.seatsTotal

    // Width as percentage
    const rightPartyWidth = (rightPartyTotal / totalSeats) * 100
    const leftPartyWidth = (leftPartyTotal / totalSeats) * 100
    const rightPartySeatsWonWidth =
        (data.right.wonSeats / rightPartyTotal) * 100
    const leftPartySeatsWonWidth = (data.left.wonSeats / leftPartyTotal) * 100
    const rightPartySeatsPredictedWidth =
        (data.right.predictedSeats / rightPartyTotal) * 100
    const leftPartySeatsPredictedWidth =
        (data.left.predictedSeats / leftPartyTotal) * 100

    let votesCountedText

    if (votesCountedPercentage !== undefined) {
        if (votesCountedPercentage === 0) {
            votesCountedText = '0%'
        } else if (votesCountedPercentage < 1) {
            votesCountedText = '< 1%'
        } else {
            votesCountedText = `${votesCountedPercentage.toFixed(1)}%`
        }
    }
    return (
        <StyledContainer>
            <StyledWrapper>
                {/* Left Party Seat Count */}
                <StyledSeatCountContainer>
                    <StyledSeatCount color={data.left.partyColors.primary}>
                        {data.left.wonSeats}
                    </StyledSeatCount>
                </StyledSeatCountContainer>

                <StyledBarsContainer>
                    {/* Center Line */}
                    <StyledCenterLine>
                        <StyledCenterLabel>
                            {data.seatsToWin} to win
                        </StyledCenterLabel>
                    </StyledCenterLine>

                    <StyledBackgroundBar />

                    {/* Left Party Bar */}
                    <StyledPartyBar width={leftPartyWidth}>
                        <StyledPartyLabel color={data.left.partyColors.primary}>
                            <StyledPartyLabelSeatCount>
                                {data.left.wonSeats}
                            </StyledPartyLabelSeatCount>{' '}
                            {data.left.partyName}
                        </StyledPartyLabel>
                        <StyledBarContent>
                            <StyledWonSeats
                                color={data.left.partyColors.primary}
                                width={leftPartySeatsWonWidth}
                            />
                            <StyledPredictedSeats
                                color={data.left.partyColors.light}
                                width={leftPartySeatsPredictedWidth}
                                show={showPredictedSeats}
                            />
                        </StyledBarContent>
                    </StyledPartyBar>

                    {/* Right Party Bar */}
                    <StyledPartyBar width={rightPartyWidth} isRight>
                        <StyledPartyLabel
                            color={data.right.partyColors.primary}
                            isRight
                        >
                            {data.right.partyName}{' '}
                            <StyledPartyLabelSeatCount>
                                {data.right.wonSeats}
                            </StyledPartyLabelSeatCount>
                        </StyledPartyLabel>
                        <StyledBarContent>
                            <StyledPredictedSeats
                                color={data.right.partyColors.light}
                                width={rightPartySeatsPredictedWidth}
                                show={showPredictedSeats}
                            />
                            <StyledWonSeats
                                color={data.right.partyColors.primary}
                                width={rightPartySeatsWonWidth}
                            />
                        </StyledBarContent>
                    </StyledPartyBar>
                </StyledBarsContainer>

                {/* Right Party Seat Count */}
                <StyledSeatCountContainer isRight>
                    <StyledSeatCount
                        color={data.right.partyColors.primary}
                        isRight
                    >
                        {data.right.wonSeats}
                    </StyledSeatCount>
                </StyledSeatCountContainer>
            </StyledWrapper>
            {showCountdown && remainingTime ? (
                <StyledCountdownTextContainer>
                    <StyledCountdownText>Count begins in</StyledCountdownText>
                    <StyledCountdownText isBold={true}>
                        {remainingTime}
                    </StyledCountdownText>
                </StyledCountdownTextContainer>
            ) : (
                votesCountedPercentage !== undefined && (
                    <StyledCountdownTextContainer>
                        <StyledVotesCountedText isBold={true}>
                            {votesCountedText}
                        </StyledVotesCountedText>
                        <StyledVotesCountedText>
                            votes counted
                        </StyledVotesCountedText>
                    </StyledCountdownTextContainer>
                )
            )}
        </StyledContainer>
    )
}
